import { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "@/api";
import { FISCAL_YEARS } from "@/constants/modules";
import { toast } from "@/lib/toast";
import {
  ArrowLeft, Plus, Search, Edit2, Trash2, Save, X, AlertCircle, BookOpen,
} from "lucide-react";

const FALLBACK_GROUPS = [
  "Revenus", "Deductions", "Revenu imposable",
  "Credits non remboursables", "Impot", "Annexes et ajustements",
  "Credits remboursables", "Retenues et acomptes", "Resultats", "Autre",
];

export default function TaxLinesBaseSubModule({ onBack }) {
  const defaultYear = FISCAL_YEARS[0] || new Date().getFullYear() - 1;
  const [year, setYear] = useState(defaultYear);
  const [jurisdiction, setJurisdiction] = useState("federal");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [adding, setAdding] = useState(false);
  const [newLine, setNewLine] = useState({ line_number: "", label: "", group: "Autre", jurisdiction: "federal" });
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tax_year: year };
      if (jurisdiction) params.jurisdiction = jurisdiction;
      if (debouncedSearch) params.q = debouncedSearch;
      const r = await api.get("/tax-lines/base", { params });
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [year, jurisdiction, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const validate = (ln) => {
    const errs = {};
    const num = String(ln.line_number || "").trim();
    if (!num) errs.line_number = "Numero requis";
    else if (!/^[A-Za-z0-9.\-]{1,10}$/.test(num)) errs.line_number = "Alphanum (max 10)";
    if (!String(ln.label || "").trim()) errs.label = "Description requise";
    if (String(ln.label || "").length > 300) errs.label = "Max 300 chars";
    return errs;
  };

  const handleAdd = async () => {
    const errs = validate(newLine);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Veuillez corriger les erreurs"); return; }
    try {
      await api.post("/tax-lines/base", {
        line_number: String(newLine.line_number).trim(),
        label: String(newLine.label).trim(),
        group: newLine.group || "Autre",
        jurisdiction: newLine.jurisdiction || "federal",
        tax_year: year,
      });
      toast.success("Ligne creee dans la base");
      setAdding(false);
      setNewLine({ line_number: "", label: "", group: "Autre", jurisdiction: "federal" });
      setErrors({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur creation");
    }
  };

  const handleUpdate = async (id) => {
    const errs = validate(editData);
    if (Object.keys(errs).length) { toast.error("Verifiez : " + Object.values(errs).join(", ")); return; }
    try {
      await api.put(`/tax-lines/base/${id}`, {
        line_number: String(editData.line_number).trim(),
        label: String(editData.label).trim(),
        group: editData.group || "Autre",
        jurisdiction: editData.jurisdiction || "federal",
      });
      toast.success("Ligne mise a jour");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette ligne du catalogue ?")) return;
    try { await api.delete(`/tax-lines/base/${id}`); toast.success("Supprimee"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Group items by section for display
  const grouped = useMemo(() => {
    const map = {};
    items.forEach(ln => {
      const g = ln.group || "Autre";
      if (!map[g]) map[g] = [];
      map[g].push(ln);
    });
    return map;
  }, [items]);

  const customCount = items.filter(i => i.is_custom).length;
  const seedCount = items.filter(i => i.is_seed).length;

  return (
    <div className="max-w-6xl mx-auto" data-testid="tax-lines-base">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-400 hover:text-gray-700" data-testid="back-to-tax-lines">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" /> Base lignes fiscales
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Catalogue de référence — {seedCount} ligne(s) du système · {customCount} personnalisée(s)
            </p>
          </div>
        </div>
        <button onClick={() => { setAdding(true); setErrors({}); }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700"
          data-testid="add-base-line-btn">
          <Plus className="w-4 h-4" /> Ajouter une ligne
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tlb-filters">
        <div>
          <label className="block text-xs font-medium mb-1">Annee</label>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tlb-year-select">
            {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Juridiction</label>
          <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tlb-jurisdiction-select">
            <option value="">Toutes</option>
            <option value="federal">Federal</option>
            <option value="provincial">Provincial (Quebec)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Recherche</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Numero ou description..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              data-testid="tlb-search" />
          </div>
        </div>
      </div>

      {/* Add form */}
      {adding && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4 space-y-2" data-testid="tlb-add-form">
          <p className="text-sm font-medium text-indigo-900">Nouvelle ligne dans le catalogue</p>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-2">
              <label className="block text-xs mb-1">Ligne *</label>
              <input type="text" value={newLine.line_number}
                onChange={e => setNewLine({...newLine, line_number: e.target.value})}
                className={`w-full border rounded-lg px-2 py-1.5 text-sm font-mono ${errors.line_number ? "border-red-300 bg-red-50" : ""}`}
                placeholder="99999" data-testid="tlb-new-number" />
              {errors.line_number && <p className="text-[10px] text-red-500 mt-0.5">{errors.line_number}</p>}
            </div>
            <div className="col-span-5">
              <label className="block text-xs mb-1">Description *</label>
              <input type="text" value={newLine.label}
                onChange={e => setNewLine({...newLine, label: e.target.value})}
                className={`w-full border rounded-lg px-2 py-1.5 text-sm ${errors.label ? "border-red-300 bg-red-50" : ""}`}
                placeholder="Description de la ligne" data-testid="tlb-new-label" />
              {errors.label && <p className="text-[10px] text-red-500 mt-0.5">{errors.label}</p>}
            </div>
            <div className="col-span-3">
              <label className="block text-xs mb-1">Section</label>
              <select value={newLine.group} onChange={e => setNewLine({...newLine, group: e.target.value})}
                className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid="tlb-new-group">
                {FALLBACK_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs mb-1">Juridiction</label>
              <select value={newLine.jurisdiction} onChange={e => setNewLine({...newLine, jurisdiction: e.target.value})}
                className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid="tlb-new-jurisdiction">
                <option value="federal">Federal</option>
                <option value="provincial">Provincial</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => { setAdding(false); setErrors({}); setNewLine({ line_number: "", label: "", group: "Autre", jurisdiction: "federal" }); }}
              className="border px-3 py-1.5 rounded-lg text-sm">Annuler</button>
            <button onClick={handleAdd}
              className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-indigo-700"
              data-testid="tlb-confirm-add">Creer</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border rounded-xl overflow-hidden" data-testid="tlb-list">
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
          <span className="text-sm font-medium">{items.length} ligne(s)</span>
          <span className="text-xs text-gray-500">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" /> Système
            <span className="inline-block w-2 h-2 rounded-full bg-indigo-500 ml-3 mr-1" /> Personnalisé
          </span>
        </div>

        <div className="divide-y max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-10 text-center text-gray-400">
              <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
            </div>
          )}
          {!loading && Object.entries(grouped).map(([group, gLines]) => (
            <div key={group}>
              <div className="px-4 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 z-10">
                {group} <span className="font-normal text-gray-400">({gLines.length})</span>
              </div>
              {gLines.map(line => {
                const id = line.id || `${line.jurisdiction}-${line.line_number}`;
                const isEditing = editingId === id;
                const isCustom = line.is_custom;
                return (
                  <div key={id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50" data-testid={`base-line-${line.line_number}`}>
                    {isEditing ? (
                      <>
                        <input type="text" value={editData.line_number || ""}
                          onChange={e => setEditData({...editData, line_number: e.target.value})}
                          className="w-20 border rounded px-2 py-1 text-sm font-mono" />
                        <input type="text" value={editData.label || ""}
                          onChange={e => setEditData({...editData, label: e.target.value})}
                          className="flex-1 border rounded px-2 py-1 text-sm" />
                        <select value={editData.group || "Autre"}
                          onChange={e => setEditData({...editData, group: e.target.value})}
                          className="w-32 border rounded px-2 py-1 text-xs">
                          {FALLBACK_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <select value={editData.jurisdiction || "federal"}
                          onChange={e => setEditData({...editData, jurisdiction: e.target.value})}
                          className="w-24 border rounded px-2 py-1 text-xs">
                          <option value="federal">Federal</option>
                          <option value="provincial">Provincial</option>
                        </select>
                        <button onClick={() => handleUpdate(id)} className="text-green-600 hover:text-green-800" data-testid={`tlb-save-${line.line_number}`}>
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${isCustom ? "bg-indigo-500" : "bg-green-500"}`} />
                        <span className="font-mono text-xs text-blue-600 w-16 flex-shrink-0">L.{line.line_number}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{line.label}</p>
                          <p className="text-[10px] text-gray-400 flex items-center gap-1.5">
                            <span>{line.jurisdiction === "federal" ? "Federal" : "Provincial"}</span>
                            {line.system && (
                              <span
                                className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold"
                                title="Ligne du systeme — modifiable mais marquee"
                                data-testid={`base-line-system-${line.line_number}`}
                              >
                                Systeme
                              </span>
                            )}
                          </p>
                        </div>
                        {isCustom ? (
                          <>
                            <button onClick={() => { setEditingId(id); setEditData({ ...line }); }}
                              className="text-gray-300 hover:text-blue-500" title="Modifier"
                              data-testid={`tlb-edit-${line.line_number}`}>
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(id)}
                              className="text-gray-300 hover:text-red-500" title="Supprimer"
                              data-testid={`tlb-delete-${line.line_number}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic" title="Ligne du système — non modifiable">
                            système
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {!loading && !items.length && (
            <div className="px-4 py-10 text-center text-gray-400">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucune ligne dans le catalogue</p>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3 flex items-start gap-1">
        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        Les lignes systèmes (vert) viennent du barème fédéral/provincial pré-installé. Ajoutez vos lignes personnalisées (indigo) pour combler les manques. Les lignes personnalisées remplacent automatiquement les lignes systèmes du même numéro.
      </p>
    </div>
  );
}
